import { com } from '@eidu/web'
import { Button, Stack } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import React, { useMemo, useState } from 'react'
import Collapsable from './Collapsable'
import SemanticLinksItem from '../input/SemanticLinksItem'
import AddSemanticLinkForm from '../input/AddSemanticLinkForm'
import SemanticLinkConfig, { fromPredefined } from '../../types/SemanticLinkConfig'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import SemanticLink = com.eidu.sharedlib.entity.analytics.SemanticLink
import SemanticLinkKey = com.eidu.sharedlib.entity.analytics.SemanticLinkKey
import allSemanticLinkKeyConfigurations = com.eidu.sharedlib.entity.analytics.allSemanticLinkKeyConfigurations
import FieldType = com.eidu.sharedlib.entity.field.FieldType

const configForKey = (key: SemanticLinkKey): SemanticLinkConfig => {
  const predefined = allSemanticLinkKeyConfigurations.asJsReadonlyArrayView().find((c) => c.key.equals(key))
  if (predefined) return fromPredefined(predefined)
  return {
    title: key.toString(),
    key,
    toTypeKind: null,
    duplicatesAllowed: true,
    // Field type isn't persisted on SemanticLink, so non-Reference custom links display
    // the target field as part of the path rather than in a separate dropdown
    fieldType: FieldType.Reference,
  }
}

type SemanticLinksStackProps = {
  entityTypes: readonly EntityType[]
  entityType: EntityType
  semanticLinks: readonly SemanticLink[]
}

const SemanticLinksStack = ({ entityTypes, entityType, semanticLinks }: SemanticLinksStackProps) => {
  const [showAddForm, setShowAddForm] = useState(false)

  const configs = useMemo(() => {
    const linksForType = semanticLinks.filter(({ path }) => path.fromType.equals(entityType.id))
    const uniqueKeys = [...new Map(linksForType.map((link) => [link.key.toString(), link.key])).values()]
    return uniqueKeys.map(configForKey)
  }, [semanticLinks, entityType.id])

  return (
    <Collapsable title={entityType.name} key={entityType.id.toString()}>
      <Stack spacing={1}>
        {configs.map((config) => (
          <SemanticLinksItem
            key={config.key.toString()}
            entityType={entityType}
            entityTypes={entityTypes}
            semanticLinks={semanticLinks}
            semanticLinkConfig={config}
          />
        ))}
        {showAddForm ? (
          <AddSemanticLinkForm
            entityType={entityType}
            entityTypes={entityTypes}
            onSave={() => setShowAddForm(false)}
            onCancel={() => setShowAddForm(false)}
          />
        ) : (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setShowAddForm(true)}>
            Add semantic link
          </Button>
        )}
      </Stack>
    </Collapsable>
  )
}

export default SemanticLinksStack
